/**
 * Theme color tokens and helpers for the design system.
 * Maps CSS custom properties from globals.css (light/dark) to JS for inline styles and class generation.
 */

export const colors = {
  background: 'var(--background)',
  'background-hover': 'var(--background-hover)',
  'background-active': 'var(--background-active)',
  foreground: 'var(--foreground)',
  'foreground-hover': 'var(--foreground-hover)',
  card: 'var(--card)',
  'card-hover': 'var(--card-hover)',
  'card-active': 'var(--card-active)',
  'card-foreground': 'var(--card-foreground)',
  popover: 'var(--popover)',
  'popover-foreground': 'var(--popover-foreground)',
  primary: 'var(--primary)',
  'primary-hover': 'var(--primary-hover)',
  'primary-active': 'var(--primary-active)',
  'primary-foreground': 'var(--primary-foreground)',
  secondary: 'var(--secondary)',
  'secondary-foreground': 'var(--secondary-foreground)',
  accent: 'var(--accent)',
  'accent-foreground': 'var(--accent-foreground)',
  destructive: 'var(--destructive)',
  teal: 'var(--teal)',
  'teal-hover': 'var(--teal-hover)',
  'teal-active': 'var(--teal-active)',
  'teal-foreground': 'var(--teal-foreground)',
  neutral: 'var(--neutral)',
  'neutral-hover': 'var(--neutral-hover)',
  'neutral-active': 'var(--neutral-active)',
  'neutral-foreground': 'var(--neutral-foreground)',
  muted: 'var(--muted)',
  'muted-hover': 'var(--muted-hover)',
  'muted-active': 'var(--muted-active)',
  'muted-foreground': 'var(--muted-foreground)',
  error: 'var(--error)',
  'error-hover': 'var(--error-hover)',
  'error-active': 'var(--error-active)',
  'error-foreground': 'var(--error-foreground)',
  success: 'var(--success)',
  'success-hover': 'var(--success-hover)',
  'success-active': 'var(--success-active)',
  'success-foreground': 'var(--success-foreground)',
  warning: 'var(--warning)',
  'warning-hover': 'var(--warning-hover)',
  'warning-active': 'var(--warning-active)',
  'warning-foreground': 'var(--warning-foreground)',
  info: 'var(--info)',
  'info-hover': 'var(--info-hover)',
  'info-active': 'var(--info-active)',
  'info-foreground': 'var(--info-foreground)',
  border: 'var(--border)',
  'border-hover': 'var(--border-hover)',
  input: 'var(--input)',
  'input-hover': 'var(--input-hover)',
  ring: 'var(--ring)',
};

export type ThemeColor = keyof typeof colors;
export const themeColorNames = Object.keys(colors) as ThemeColor[];

export const colorClassPrefixes = [
  'text',
  'bg',
  'border',
  'ring',
  'outline',
  'decoration',
  'fill',
  'stroke',
] as const;
export type ColorClassPrefix = (typeof colorClassPrefixes)[number];
export type ThemeColorClass = `${ColorClassPrefix}-${ThemeColor}`;

export const themeColorClasses = colorClassPrefixes.flatMap((prefix) =>
  themeColorNames.map((color) => `${prefix}-${color}` as ThemeColorClass)
);

export type ThemeColorProp = ThemeColor | ThemeColorClass;

/**
 * Core color variants used across all components
 * Use this type for component `variant` props to ensure consistency
 * Includes: neutral, primary, teal, info, success, warning, error
 */
export type ColorVariant =
  | 'neutral'
  | 'primary'
  | 'muted'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'teal';

/**
 * Notification/alert-specific color variants (excludes neutral/primary/teal)
 * For feedback components like Alert, Toast, etc.
 */
export type NotificationColorVariant = Exclude<
  ColorVariant,
  'neutral' | 'primary' | 'muted' | 'teal'
>;

/**
 * Common color variants for most components (the essential 5)
 * Includes: neutral, primary, success, warning, error
 */
export type CommonColorVariant = Extract<
  ColorVariant,
  'neutral' | 'primary' | 'success' | 'warning' | 'error'
>;

const outlineBaseClasses =
  'border-2 bg-transparent transition-colors transition-shadow duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent' as const;

/**
 * Type-safe record ensuring all color variants are defined for each style
 */
type ColorVariantRecord = Record<ColorVariant, string>;

/**
 * Standardized color variant classes for consistent component styling
 * All variants support: neutral, primary, teal, info, success, warning, error
 */
export const colorVariants: {
  solid: ColorVariantRecord;
  outline: ColorVariantRecord;
  ghost: ColorVariantRecord;
  soft: ColorVariantRecord;
  link: ColorVariantRecord;
  none: ColorVariantRecord;
  badge: ColorVariantRecord & { inverted: ColorVariantRecord };
  alert: ColorVariantRecord;
  breadcrumb: { link: ColorVariantRecord; active: ColorVariantRecord };
  dot: ColorVariantRecord;
} = {
  // Solid buttons/elements - filled background
  solid: {
    neutral:
      'border-2 border-transparent bg-neutral text-neutral-foreground hover:bg-neutral-hover active:bg-neutral-active',
    primary:
      'border-2 border-transparent bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active',
    muted:
      'border-2 border-transparent bg-muted text-muted-foreground hover:bg-muted-hover active:bg-muted-active',
    teal: 'border-2 border-transparent bg-teal text-teal-foreground hover:bg-teal-hover active:bg-teal-active',
    info: 'border-2 border-transparent bg-info text-info-foreground hover:bg-info-hover active:bg-info-active',
    success:
      'border-2 border-transparent bg-success text-success-foreground hover:bg-success-hover active:bg-success-active',
    warning:
      'border-2 border-transparent bg-warning text-warning-foreground hover:bg-warning-hover active:bg-warning-active',
    error:
      'border-2 border-transparent bg-error text-error-foreground hover:bg-error-hover active:bg-error-active',
  },

  // Outline style - transparent with border
  outline: {
    neutral: `${outlineBaseClasses} border-neutral text-neutral hover:border-neutral-hover hover:text-neutral-hover hover:shadow-[inset_0_0_0_1px_var(--neutral))] focus-visible:shadow-[inset_0_0_0_1px_var(--neutral))] focus-visible:outline-neutral/3`,
    primary: `${outlineBaseClasses} border-primary text-primary hover:border-primary-hover hover:text-primary-hover hover:shadow-[inset_0_0_0_1px_var(--primary))] focus-visible:shadow-[inset_0_0_0_1px_var(--primary))] focus-visible:outline-primary/3`,
    muted: `${outlineBaseClasses} border-muted text-muted-foreground hover:border-muted-hover hover:text-muted-foreground hover:shadow-[inset_0_0_0_1px_var(--muted))] focus-visible:shadow-[inset_0_0_0_1px_var(--muted))] focus-visible:outline-muted/3`,
    teal: `${outlineBaseClasses} border-teal text-teal hover:border-teal-hover hover:text-teal-hover hover:shadow-[inset_0_0_0_1px_var(--teal))] focus-visible:shadow-[inset_0_0_0_1px_var(--teal))] focus-visible:outline-teal/3`,
    info: `${outlineBaseClasses} border-info text-info hover:border-info-hover hover:text-info-hover hover:shadow-[inset_0_0_0_1px_var(--info))] focus-visible:shadow-[inset_0_0_0_1px_var(--info))] focus-visible:outline-info/3`,
    success: `${outlineBaseClasses} border-success text-success hover:border-success-hover hover:text-success-hover hover:shadow-[inset_0_0_0_1px_var(--success))] focus-visible:shadow-[inset_0_0_0_1px_var(--success))] focus-visible:outline-success/3`,
    warning: `${outlineBaseClasses} border-warning text-warning hover:border-warning-hover hover:text-warning-hover hover:shadow-[inset_0_0_0_1px_var(--warning))] focus-visible:shadow-[inset_0_0_0_1px_var(--warning))] focus-visible:outline-warning/3`,
    error: `${outlineBaseClasses} border-error text-error hover:border-error-hover hover:text-error-hover hover:shadow-[inset_0_0_0_1px_var(--error))] focus-visible:shadow-[inset_0_0_0_1px_var(--error))] focus-visible:outline-error/5`,
  },

  // Ghost style - transparent with subtle hover
  ghost: {
    neutral:
      'border-2 border-transparent bg-transparent text-foreground hover:bg-neutral/10 active:bg-neutral/20',
    primary:
      'border-2 border-transparent bg-transparent text-primary hover:bg-primary/10 active:bg-primary/20',
    muted:
      'border-2 border-transparent bg-transparent text-muted-foreground hover:bg-muted/10 active:bg-muted/20',
    teal: 'border-2 border-transparent bg-transparent text-teal hover:bg-teal/10 active:bg-teal/20',
    info: 'border-2 border-transparent bg-transparent text-info hover:bg-info/10 active:bg-info/20',
    success:
      'border-2 border-transparent bg-transparent text-success hover:bg-success/10 active:bg-success/20',
    warning:
      'border-2 border-transparent bg-transparent text-warning hover:bg-warning/10 active:bg-warning/20',
    error:
      'border-2 border-transparent bg-transparent text-error hover:bg-error/10 active:bg-error/20',
  },

  // Soft style - subtle background
  soft: {
    neutral:
      'border-2 border-transparent bg-neutral/20 text-neutral hover:bg-neutral/30 active:bg-neutral/40',
    primary:
      'border-2 border-transparent bg-primary/20 text-primary hover:bg-primary/30 active:bg-primary/40',
    muted:
      'border-2 border-transparent bg-muted/20 text-muted-foreground hover:bg-muted/30 active:bg-muted/40',
    teal: 'border-2 border-transparent bg-teal/20 text-teal hover:bg-teal/30 active:bg-teal/40',
    info: 'border-2 border-transparent bg-info/20 text-info hover:bg-info/30 active:bg-info/40',
    success:
      'border-2 border-transparent bg-success/20 text-success hover:bg-success/30 active:bg-success/40',
    warning:
      'border-2 border-transparent bg-warning/20 text-warning hover:bg-warning/30 active:bg-warning/40',
    error:
      'border-2 border-transparent bg-error/20 text-error hover:bg-error/30 active:bg-error/40',
  },

  // None style - fully transparent, no background on any state
  none: {
    neutral:
      'border-2 border-transparent bg-transparent text-foreground hover:bg-transparent active:bg-transparent',
    primary:
      'border-2 border-transparent bg-transparent text-primary hover:bg-transparent active:bg-transparent',
    muted:
      'border-2 border-transparent bg-transparent text-muted-foreground hover:bg-transparent active:bg-transparent',
    teal: 'border-2 border-transparent bg-transparent text-teal hover:bg-transparent active:bg-transparent',
    info: 'border-2 border-transparent bg-transparent text-info hover:bg-transparent active:bg-transparent',
    success:
      'border-2 border-transparent bg-transparent text-success hover:bg-transparent active:bg-transparent',
    warning:
      'border-2 border-transparent bg-transparent text-warning hover:bg-transparent active:bg-transparent',
    error:
      'border-2 border-transparent bg-transparent text-error hover:bg-transparent active:bg-transparent',
  },

  // Link style - text only with underline on hover
  link: {
    neutral:
      'border-2 border-transparent bg-transparent text-foreground underline-offset-4 hover:underline',
    primary:
      'border-2 border-transparent bg-transparent text-primary underline-offset-4 hover:underline',
    muted:
      'border-2 border-transparent bg-transparent text-muted-foreground underline-offset-4 hover:underline',
    teal: 'border-2 border-transparent bg-transparent text-teal underline-offset-4 hover:underline',
    info: 'border-2 border-transparent bg-transparent text-info underline-offset-4 hover:underline',
    success:
      'border-2 border-transparent bg-transparent text-success underline-offset-4 hover:underline',
    warning:
      'border-2 border-transparent bg-transparent text-warning underline-offset-4 hover:underline',
    error:
      'border-2 border-transparent bg-transparent text-error underline-offset-4 hover:underline',
  },

  // Badge/Chip style - subtle filled background with border
  badge: {
    neutral: 'bg-neutral/5 text-foreground border border-foreground',
    primary: 'bg-primary/5 text-primary border border-primary',
    muted: 'bg-muted/5 text-muted-foreground border border-muted',
    teal: 'bg-teal/5 text-teal border border-teal',
    info: 'bg-info/5 text-info border border-info',
    success: 'bg-success/5 text-success border border-success',
    warning: 'bg-warning/5 text-warning border border-warning',
    error: 'bg-error/5 text-error border border-error',
    inverted: {
      neutral: 'bg-neutral/40 text-neutral-foreground border border-neutral',
      primary:
        'bg-primary/90 text-primary-foreground border border-primary-foreground',
      muted: 'bg-muted/90 text-muted-foreground border border-muted',
      teal: 'bg-teal/90 text-teal-foreground border border-teal',
      info: 'bg-info/90 text-info-foreground border border-info',
      success: 'bg-success/90 text-success-foreground border border-success',
      warning: 'bg-warning/90 text-warning-foreground border border-warning',
      error: 'bg-error/90 text-error-foreground border border-error',
    },
  },

  // Alert style - translucent background with border
  alert: {
    neutral:
      'bg-neutral/30 text-foreground border-neutral/50 backdrop-blur-3xl',
    primary:
      'bg-primary/30 text-foreground border-primary/50 backdrop-blur-3xl',
    muted: 'bg-muted/30 text-foreground border-muted/50 backdrop-blur-3xl',
    teal: 'bg-teal/30 text-foreground border-teal/50 backdrop-blur-3xl',
    info: 'bg-info/30 text-foreground border-info/50 backdrop-blur-3xl',
    success:
      'bg-success/30 text-foreground border-success/50 backdrop-blur-3xl',
    warning:
      'bg-warning/30 text-foreground border-warning/50 backdrop-blur-3xl',
    error: 'bg-error/30 text-foreground border-error/50 backdrop-blur-3xl',
  },

  // Breadcrumb style - link and active states
  breadcrumb: {
    link: {
      neutral:
        'text-neutral hover:text-neutral-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-neutral-ring rounded px-1 -mx-1',
      primary:
        'text-primary hover:text-primary-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-ring rounded px-1 -mx-1',
      muted:
        'text-muted-foreground hover:text-muted-foreground hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-muted-ring rounded px-1 -mx-1',
      teal: 'text-teal hover:text-teal-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-ring rounded px-1 -mx-1',
      info: 'text-info hover:text-info-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-info-ring rounded px-1 -mx-1',
      success:
        'text-success hover:text-success-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-success-ring rounded px-1 -mx-1',
      warning:
        'text-warning hover:text-warning-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-warning-ring rounded px-1 -mx-1',
      error:
        'text-error hover:text-error-hover hover:underline cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-error-ring rounded px-1 -mx-1',
    },
    active: {
      neutral: 'text-neutral-active font-medium',
      primary: 'text-primary-active font-medium',
      muted: 'text-muted-active font-medium',
      teal: 'text-teal-active font-medium',
      info: 'text-info-active font-medium',
      success: 'text-success-active font-medium',
      warning: 'text-warning-active font-medium',
      error: 'text-error-active font-medium',
    },
  },

  // Dot style - filled circular indicator (for steppers, timelines, status dots).
  // Pair with `dotIdle` below for pending/skipped states that should render as an outlined placeholder.
  dot: {
    neutral: 'bg-neutral border-neutral text-neutral-foreground',
    primary: 'bg-primary border-primary text-primary-foreground',
    muted: 'bg-muted border-muted text-muted-foreground',
    teal: 'bg-teal border-teal text-teal-foreground',
    info: 'bg-info border-info text-info-foreground',
    success: 'bg-success border-success text-success-foreground',
    warning: 'bg-warning border-warning text-warning-foreground',
    error: 'bg-error border-error text-error-foreground',
  },
};

/**
 * Idle (pending/skipped) style for dot-like indicators.
 * Pairs with `colorVariants.dot` so callers can render "not yet reached" states consistently.
 */
export const dotIdle =
  'bg-background border-border text-muted-foreground' as const;

/** Standalone background class per color variant. */
export const bgColor: Record<ColorVariant, string> = {
  neutral: 'bg-neutral',
  primary: 'bg-primary',
  muted: 'bg-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
  teal: 'bg-teal',
};

/** Standalone text class per color variant. */
export const textColor: Record<ColorVariant, string> = {
  neutral: 'text-neutral',
  primary: 'text-primary',
  muted: 'text-muted',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
  info: 'text-info',
  teal: 'text-teal',
};

/** Ring-foreground class per color variant. */
export const ringColor: Record<ColorVariant, string> = {
  neutral: 'ring-neutral-foreground',
  primary: 'ring-primary-foreground',
  muted: 'ring-muted-foreground',
  success: 'ring-success-foreground',
  warning: 'ring-warning-foreground',
  error: 'ring-error-foreground',
  info: 'ring-info-foreground',
  teal: 'ring-teal-foreground',
};

export type ColorVariantStyle = keyof typeof colorVariants;
export type ColorVariantKey<T extends ColorVariantStyle> =
  keyof (typeof colorVariants)[T];

export const radii = {
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
};

export const fonts = {
  sans: "'Inter', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

export const alignments = ['left', 'center', 'right'] as const;
export type Alignment = (typeof alignments)[number];

export const inputAlignClasses: Record<Alignment, string> = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
};

/**
 * HTML autocomplete attribute values for form inputs
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
 */
export const autocompleteOptions = [
  'on',
  'off',
  'address-line1',
  'address-line2',
  'address-line3',
  'address-level1',
  'address-level2',
  'address-level3',
  'address-level4',
  'street-address',
  'country',
  'country-name',
  'postal-code',
  'name',
  'additional-name',
  'family-name',
  'given-name',
  'honoric-prefix',
  'honoric-suffix',
  'nickname',
  'organization-title',
  'username',
  'new-password',
  'current-password',
  'bday',
  'bday-day',
  'bday-month',
  'bday-year',
  'sex',
  'one-time-code',
  'organization',
  'cc-name',
  'cc-given-name',
  'cc-additional-name',
  'cc-family-name',
  'cc-number',
  'cc-exp',
  'cc-exp-month',
  'cc-exp-year',
  'cc-csc',
  'cc-type',
  'transaction-currency',
  'transaction-amount',
  'language',
  'url',
  'email',
  'photo',
  'tel',
  'tel-country-code',
  'tel-national',
  'tel-area-code',
  'tel-local',
  'tel-local-prefix',
  'tel-local-suffix',
  'tel-extension',
  'impp',
] as const;

export type AutocompleteOption = (typeof autocompleteOptions)[number];

// ---------------------------------------------------------------------------
// Badge tokens
// ---------------------------------------------------------------------------

export type BadgeRadius = 'none' | 'sm' | 'md' | 'lg' | 'full';

export const badgeRadiusClasses: Record<BadgeRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

export const badgeVariantText: Record<ColorVariant, string> = {
  neutral: 'text-foreground',
  primary: 'text-primary',
  muted: 'text-muted-foreground',
  teal: 'text-teal',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

export const badgeVariantTextInverted: Record<ColorVariant, string> = {
  neutral: 'text-neutral-foreground',
  primary: 'text-primary-foreground',
  muted: 'text-muted-foreground',
  teal: 'text-teal-foreground',
  info: 'text-info-foreground',
  success: 'text-success-foreground',
  warning: 'text-warning-foreground',
  error: 'text-error-foreground',
};

export const badgeVariantBg: Record<ColorVariant, string> = {
  neutral: 'bg-neutral/5',
  primary: 'bg-primary/5',
  muted: 'bg-muted/5',
  teal: 'bg-teal/5',
  info: 'bg-info/5',
  success: 'bg-success/5',
  warning: 'bg-warning/5',
  error: 'bg-error/5',
};

export const badgeVariantBgInverted: Record<ColorVariant, string> = {
  neutral: 'bg-neutral/40',
  primary: 'bg-primary/90',
  muted: 'bg-muted/90',
  teal: 'bg-teal/90',
  info: 'bg-info/90',
  success: 'bg-success/90',
  warning: 'bg-warning/90',
  error: 'bg-error/90',
};

export const badgeVariantBorder: Record<ColorVariant, string> = {
  neutral: 'border-foreground',
  primary: 'border-primary',
  muted: 'border-muted',
  teal: 'border-teal',
  info: 'border-info',
  success: 'border-success',
  warning: 'border-warning',
  error: 'border-error',
};

export const badgeVariantInteractive: Record<ColorVariant, string> = {
  neutral:
    'cursor-pointer hover:bg-neutral/10 hover:text-neutral-hover hover:border-neutral-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-ring',
  primary:
    'cursor-pointer hover:bg-primary/10 hover:text-primary-hover hover:border-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring',
  muted:
    'cursor-pointer hover:bg-muted/10 hover:text-muted-foreground hover:border-muted-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-muted-ring',
  teal: 'cursor-pointer hover:bg-teal/10 hover:text-teal-hover hover:border-teal-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-ring',
  info: 'cursor-pointer hover:bg-info/10 hover:text-info-hover hover:border-info-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-ring',
  success:
    'cursor-pointer hover:bg-success/10 hover:text-success-hover hover:border-success-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-ring',
  warning:
    'cursor-pointer hover:bg-warning/10 hover:text-warning-hover hover:border-warning-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-ring',
  error:
    'cursor-pointer hover:bg-error/10 hover:text-error-hover hover:border-error-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-ring',
};
